import React, { useRef, useState } from 'react';
import { Grid, makeStyles, IconButton, FormHelperText } from '@material-ui/core';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { getIn } from 'formik';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { parseHandler } from 'core/helpers/csv-header-validation';
import BaseDialog from 'core/components/base-dialog';
import { useBulkTaskRequiredCsvHeaders } from 'tools/hooks/use-bulk-task-required-csv-headers';

const useStyles = makeStyles((theme) => ({
  uploadButton: {
    marginBottom: 20,
    marginRight: 10
  },
  templateButton: {
    color: theme.colors.light_text,
    fontSize: 14
  },
  alertLabel: {
    paddingBottom: theme.spacing(4)
  },
  templateButtonAlert: {
    fontSize: 14
  }
}));

// file can be used to validate Headers of a CSV file upon upload of a local file.
// When first created, only applied to tools bulk task, so the constants for required task are only located there at the moment
// If using this outside of tools, a new constants array will need to be created to pass the required task

const CsvFile = ({
  field: { id, name, label, mode, optional, onChange: customOnChange, value: csvFile, ...props },
  ctx: { setFieldTouched, setFieldValue, errors }
}) => {
  const fileRef = useRef(null);
  const classes = useStyles();
  const error = getIn(errors, name);

  const [open, setOpen] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const missingHeaders = missingFields?.map((header, index) => <li key={index}>{header}</li>);
  const toolHeaders = useBulkTaskRequiredCsvHeaders();

  // The displayed filename comes from the Formik value itself — a separate
  // display state can show a filename while the form no longer holds the File
  // (the ghost-run trap). Rejecting the upload must also clear the form value,
  // or the invalid file would still be submitted — and the DOM input, or
  // re-picking the corrected file under the same name would not fire onChange.
  const resetUpload = () => {
    setOpen(false);
    setFieldValue(name, null);
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };
  return (
    <>
      <input
        ref={fileRef}
        id="file-upload"
        name="csv-file-upload"
        label="csv-file-upload"
        type="file"
        style={{ display: 'none' }}
        accept=".csv"
        onChange={({ target }) => {
          setFieldTouched(name, true, false);
          setFieldValue(name, target.files[0]);
          parseHandler({ csv: target.files[0] }, setOpen, setMissingFields, toolHeaders);
        }}
      />
      <Grid container alignItems="center">
        <Button
          onClick={() => fileRef.current.click()}
          variant="contained"
          color="primary"
          className={classes.uploadButton}
        >
          Upload
        </Button>

        <Label type="toolDescription">
          <Grid container alignItems="center">
            {csvFile ? `${csvFile.name}` : 'No file selected'}
            {props.templateUrl && !csvFile && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(props.templateUrl, '_blank');
                }}
                className={classes.templateButton}
              >
                {' |   '}
                <img src={externalLinkIcon} alt="edit" />
                Start with a template
              </IconButton>
            )}
          </Grid>
        </Label>
      </Grid>
      {error && (
        <FormHelperText classes={{ root: classes.helperText }} id={id} error>
          {error}
        </FormHelperText>
      )}
      {open && (
        <BaseDialog onClose={resetUpload} open={open} size="sm">
          <Label type="subtitle" className={classes.alertLabel}>
            Uploaded CSV missing the following required header columns:
          </Label>
          <Grid className={classes.alertLabel}>{missingHeaders}</Grid>
          <Label type="greenSmall" className={classes.alertLabel}>
            Please upload a valid CSV file with all of the required parameters.{' '}
          </Label>
          <Grid container spacing={4} justifyContent="flex-end">
            <Grid>
              {props.templateUrl && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(props.templateUrl, '_blank');
                  }}
                  className={classes.templateButtonAlert}
                >
                  <img src={externalLinkIcon} alt="edit" />
                  Use a template
                </IconButton>
              )}
              <Button variant="contained" color="secondary" onClick={resetUpload}>
                Upload new file
              </Button>
            </Grid>
          </Grid>
        </BaseDialog>
      )}
    </>
  );
};

export default CsvFile;
